import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import styles from './DeleteAccount.module.css';

// ─── Step list sub-component ──────────────────────────────────────────────────
function StepList({ steps }) {
    return (
        <ol className={styles.stepList}>
            {steps.map((step, i) => (
                <li key={i} className={styles.stepItem}>
                    <span className={styles.stepNumber}>{i + 1}</span>
                    <span className={styles.stepText}>{step}</span>
                </li>
            ))}
        </ol>
    );
}

// ─── Bullet list sub-component ───────────────────────────────────────────────
function BulletList({ items }) {
    return (
        <ul className={styles.bulletList}>
            {items.map((item, i) => (
                <li key={i} className={styles.bulletItem}>
                    <span className={styles.bulletDot} aria-hidden="true">•</span>
                    <span>{item}</span>
                </li>
            ))}
        </ul>
    );
}

// ─── Section card sub-component ───────────────────────────────────────────────
function Section({ number, heading, children }) {
    return (
        <section className={styles.section}>
            <div className={styles.sectionHeader}>
                <span className={styles.sectionNumber}>{number}</span>
                <h2 className={styles.sectionHeading}>{heading}</h2>
            </div>
            <div className={styles.sectionBody}>{children}</div>
        </section>
    );
}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function DeleteAccount() {
    return (
        <>
            <Helmet>
                <title>Delete Your Account | Aluvo</title>
                <meta
                    name="description"
                    content="Request deletion of your Aluvo account and associated data. Learn what gets deleted, what is retained, and how to submit your request."
                />
                {/* Apple requires this page to be publicly reachable, and the
                    server-rendered version at /delete-account is indexable —
                    keep the client copy in step rather than de-indexing it. */}
                <meta name="robots" content="index, follow" />
            </Helmet>

            <main className={styles.page}>
                <div className={styles.container}>

                    {/* ── Header ── */}
                    <header className={styles.hero}>
                        <div className={styles.heroIcon} aria-hidden="true">🗑️</div>
                        <h1 className={styles.heroTitle}>Delete Your Aluvo Account</h1>
                        <p className={styles.heroSubtitle}>
                            We're sorry to see you go. Here's everything you need to know about
                            deleting your account and data.
                        </p>
                    </header>

                    {/* ── Section 1 — How to delete ── */}
                    <Section number="01" heading="How to Request Account Deletion">
                        <p className={styles.bodyText}>
                            There are two ways to request deletion of your Aluvo account.
                        </p>

                        {/* Option 1 */}
                        <div className={styles.optionCard}>
                            <div className={styles.optionLabelRow}>
                                <span className={styles.optionBadge}>Option 1 — Preferred</span>
                                <span className={styles.optionTag}>In-app</span>
                            </div>
                            <p className={styles.bodyText}>
                                The fastest way to delete your account is directly within the Aluvo app:
                            </p>
                            <StepList steps={[
                                'Open the Aluvo app',
                                'Go to Profile',
                                'Tap Settings',
                                'Tap "Delete Account"',
                                'Confirm your deletion request',
                            ]} />
                        </div>

                        {/* Option 2 */}
                        <div className={styles.optionCard}>
                            <div className={styles.optionLabelRow}>
                                <span className={styles.optionBadge}>Option 2</span>
                                <span className={styles.optionTag}>By email</span>
                            </div>
                            <p className={styles.bodyText}>
                                You can also request deletion by emailing us directly:
                            </p>
                            <div className={styles.emailInstructions}>
                                <div className={styles.emailRow}>
                                    <span className={styles.emailLabel}>Email</span>
                                    <a href="mailto:support@aluvo.app" className={styles.emailLink}>
                                        support@aluvo.app
                                    </a>
                                </div>
                                <div className={styles.emailRow}>
                                    <span className={styles.emailLabel}>Subject</span>
                                    <span className={styles.emailValue}>"Account Deletion Request"</span>
                                </div>
                                <div className={styles.emailRow}>
                                    <span className={styles.emailLabel}>Body</span>
                                    <span className={styles.emailValue}>Include your registered email address</span>
                                </div>
                            </div>
                            <p className={styles.noteText}>
                                We will process your request within 30 days.
                            </p>
                        </div>
                    </Section>

                    {/* ── Section 2 — What gets deleted ── */}
                    <Section number="02" heading="What Gets Deleted">
                        <p className={styles.bodyText}>
                            When you delete your Aluvo account, the following data is permanently removed:
                        </p>
                        <BulletList items={[
                            'Your account and login credentials',
                            'Your display name and username (@handle)',
                            'Your entire coin collection and all earned coins (Bronze, Silver, Gold, and Legendary)',
                            'Your collection progress and completed collections',
                            'Your watchlist',
                            'Your followers and following relationships',
                            'Your reactions and feed activity',
                            'Your profile avatar and bio',
                            'Your challenge history and verification records',
                        ]} />
                        <div className={styles.warningBox}>
                            <span className={styles.warningIcon} aria-hidden="true">⚠️</span>
                            <p className={styles.warningText}>
                                This data cannot be recovered once deleted.
                            </p>
                        </div>
                    </Section>

                    {/* ── Section 3 — What is retained ── */}
                    <Section number="03" heading="What We Retain">
                        <p className={styles.bodyText}>
                            Some data may be retained after deletion for legal, security, and operational purposes:
                        </p>
                        <BulletList items={[
                            'Anonymized, aggregate usage data (not linked to your identity) may be retained for up to 90 days for analytics and product improvement purposes',
                            'Server logs may be retained for up to 30 days for security and fraud prevention',
                            'If required by law or to resolve disputes, certain data may be retained for longer periods as required by applicable law',
                        ]} />
                        <div className={styles.infoBox}>
                            <p className={styles.infoText}>
                                Retained data is not linked to your personal identity and cannot be used to identify you.
                            </p>
                        </div>
                    </Section>

                    {/* ── Section 4 — Processing time ── */}
                    <Section number="04" heading="Processing Time">
                        <p className={styles.bodyText}>
                            Account deletion requests submitted in-app are processed immediately. Requests submitted
                            by email are processed within 30 days of receipt. You will receive a confirmation email
                            once your account has been deleted.
                        </p>
                    </Section>

                    {/* ── Section 5 — Questions ── */}
                    <Section number="05" heading="Questions?">
                        <p className={styles.bodyText}>
                            If you have any questions about account deletion or your data, contact us at{' '}
                            <a href="mailto:support@aluvo.app" className={styles.emailLink}>
                                support@aluvo.app
                            </a>
                        </p>
                    </Section>

                    {/* ── Page footer ── */}
                    <footer className={styles.pageFooter}>
                        <div className={styles.footerLinks}>
                            <Link to="/privacy" className={styles.footerLink}>Privacy Policy</Link>
                            <span className={styles.footerDot} aria-hidden="true">·</span>
                            <Link to="/support" className={styles.footerLink}>Support</Link>
                        </div>
                        <p className={styles.footerCopy}>© 2026 Ornaah. All rights reserved.</p>
                    </footer>

                </div>
            </main>
        </>
    );
}